/**
 * Workspace360 — shell de layout de la Vista/Formulario 360.
 *
 * Modelo "scroll de documento": la columna derecha (`.workspace`) es el contenedor
 * que scrollea; el header queda sticky arriba y las barras internas (toolbar del
 * centro, header del panel) se pegan debajo del header usando la variable
 * `--head-h`, que se mide en vivo con un ResizeObserver sobre el header real.
 *
 * Slots:
 *   - `rail`    columna izquierda colapsable (lista de documentos del filtro actual)
 *   - `header`  cabecera sticky del documento (título, totales, acciones)
 *   - `chips`   tira de chips de estado (opcional)
 *   - `center`  panel central conmutable (PDF / Detalle / XML)
 *   - `panel`   panel contextual derecho (relacionados)
 *
 * Responsive: ≤1100 el panel baja bajo el centro; ≤860 se oculta el rail; ≤600
 * el header deja de ser sticky. Theming con tokens semánticos (light + dark).
 */
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'

interface Workspace360Props {
  header: ReactNode
  center: ReactNode
  rail?: ReactNode
  railCollapsed?: boolean
  chips?: ReactNode
  panel?: ReactNode
  /** Alto del shell. Por defecto ocupa el área disponible del layout. */
  className?: string
}

export function Workspace360({
  header,
  center,
  rail,
  railCollapsed = false,
  chips,
  panel,
  className = '',
}: Workspace360Props) {
  const headerRef = useRef<HTMLDivElement>(null)
  const [headH, setHeadH] = useState(130)

  // Mide la altura real del header para offsetear las barras sticky internas.
  useLayoutEffect(() => {
    const el = headerRef.current
    if (!el) return
    const update = () => setHeadH(el.offsetHeight)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Columnas del grid externo según presencia/colapso del rail.
  const railCols = rail
    ? railCollapsed
      ? 'grid-cols-[56px_1fr] max-[1100px]:grid-cols-[56px_1fr] max-[860px]:grid-cols-1'
      : 'grid-cols-[264px_1fr] max-[1100px]:grid-cols-[220px_1fr] max-[860px]:grid-cols-1'
    : 'grid-cols-1'

  return (
    <div className={`grid h-full min-h-0 ${railCols} ${className}`}>
      {rail && (
        <aside className="three60-rail-in bg-surface border-r border-divider flex flex-col overflow-hidden max-[860px]:hidden">
          {rail}
        </aside>
      )}

      <section
        className="three60-workspace-in workspace flex flex-col overflow-y-auto scrollbar-slim bg-canvas min-h-0"
        style={{ ['--head-h' as string]: `${headH}px` }}
      >
        <div
          ref={headerRef}
          className="sticky top-0 z-20 bg-surface border-b border-divider max-[600px]:static"
        >
          {header}
        </div>

        {chips && <div className="px-[18px] pt-3.5 pb-1.5">{chips}</div>}

        <div
          className={[
            'grid items-start gap-3.5 px-[18px] pt-1.5 pb-[18px]',
            panel ? 'grid-cols-[1fr_300px] max-[1100px]:grid-cols-1' : 'grid-cols-1',
            'max-[860px]:px-3',
          ].join(' ')}
        >
          <div className="min-w-0">{center}</div>
          {panel && <div className="min-w-0">{panel}</div>}
        </div>
      </section>
    </div>
  )
}
